import React from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import { showSuccess } from "@infrastructure/api/utils/toastNotifications";
import { ErrorResponse } from "@core/types/errorResponse";
import { useApiError } from "@infrastructure/api/hooks/useApiError";
import { useReportTagActions } from "@features/report/tag/hooks/useReportTagActions";

export type ReopenActionMode = "approve" | "deny";

const ApproveOrDenyReopenModal: React.FC<{
    show: boolean;
    onHide: () => void;
    tag: { report_tag_id: number; name: string } | null;
    nationId: number;
    mode: ReopenActionMode;
    onCompleted?: () => void;
}> = ({ show, onHide, tag, nationId, mode, onCompleted }) => {
    const showError = useApiError();
    const { busy, doApproveReopen, doDenyReopen } = useReportTagActions();

    const isApprove = mode === "approve";

    const handleSubmit = async () => {
        if (!tag) return;

        try {
            const action = isApprove ? doApproveReopen : doDenyReopen;
            const response = await action(nationId, tag.report_tag_id);

            if (response.isSuccess()) {
                showSuccess(
                    response.getData()?.message ??
                        (isApprove ? "Reopen approved." : "Reopen denied.")
                );
                onHide();
                onCompleted?.();
            } else {
                showError(response.getError() as ErrorResponse);
            }
        } catch (error) {
            showError(error as ErrorResponse);
        }
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            backdrop="static"
            keyboard={false}
            centered
            size="lg"
        >
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="fw-semibold">
                    {isApprove ? "Approve Reopen" : "Deny Reopen"}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-3">
                <p>
                    Are you sure you want to{" "}
                    <strong>{isApprove ? "approve" : "deny"}</strong> the reopen
                    request for tag <strong>{tag?.name}</strong>?
                </p>
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0">
                <Button variant="outline-secondary" onClick={onHide} disabled={busy}>
                    Cancel
                </Button>
                <Button
                    variant={isApprove ? "success" : "danger"}
                    size="lg"
                    className="btn-action"
                    onClick={handleSubmit}
                    disabled={busy}
                    aria-busy={busy}
                >
                    {busy ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            {isApprove ? "Approving..." : "Denying..."}
                        </>
                    ) : isApprove ? (
                        "Approve"
                    ) : (
                        "Deny"
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ApproveOrDenyReopenModal;
